import { useCallback } from 'react';

import { getPaymentErrorContent, useAfterPurchaseModalStore } from '@/entities/afterPurchase';
import { usePendingPaymentSession } from '@/entities/paymentSession';
import { usePurchaseConfirmationModalStore } from '@/entities/purchaseConfirmation';

import { API } from '@/shared/api';
import { useInterfaceLanguage } from '@/shared/i18n';

type PlanChangeSuccessDescriptions = {
  noPayment: string;
  paid: string;
};

type UsePlanChangeConfirmationArgs = {
  onClosedAfterProcessing: () => void;
};

type OpenPlanChangeConfirmationArgs = {
  confirmButtonText: string;
  errorMessage: string;
  planId: string;
  priceId: string;
  successDescriptions: PlanChangeSuccessDescriptions;
  onConfirmAnalytics: () => void;
};

export const usePlanChangeConfirmation = ({ onClosedAfterProcessing }: UsePlanChangeConfirmationArgs) => {
  const openConfirmation = usePurchaseConfirmationModalStore((state) => state.open);
  const { openError } = useAfterPurchaseModalStore();
  const { t } = useInterfaceLanguage();
  const { resolvePaymentResult, showPendingInvoiceIfAny } = usePendingPaymentSession({
    onClosedAfterProcessing,
  });

  return useCallback(
    ({
      confirmButtonText,
      errorMessage,
      planId,
      priceId,
      successDescriptions,
      onConfirmAnalytics,
    }: OpenPlanChangeConfirmationArgs) => {
      openConfirmation({
        confirmButtonText,
        promoCodeConfig: {
          enabled: true,
          planId,
        },
        fetchPreview: (promoCode) =>
          API.getPlanInvoicePreview({
            price_id: priceId,
            promotion_code: promoCode ?? undefined,
          }),
        onConfirm: async (invoice, promoCode) => {
          if (await showPendingInvoiceIfAny()) return;

          try {
            const res = await API.updatePlan({
              price_id: priceId,
              promotion_code: promoCode ?? undefined,
            });

            const amountDueToday = invoice?.instant_payment?.amount ?? 0;
            const successDescription = amountDueToday === 0 ? successDescriptions.noPayment : successDescriptions.paid;

            await resolvePaymentResult({
              response: res,
              showInvoiceLink: !!amountDueToday,
              successDescription,
            });
          } catch (error) {
            console.error(errorMessage, error);
            const errorContent = getPaymentErrorContent(undefined, t);
            openError(errorContent.message, '', errorContent.buttonText);
            throw error;
          }
        },
        onConfirmAnalytics,
      });
    },
    [openConfirmation, openError, resolvePaymentResult, showPendingInvoiceIfAny, t],
  );
};
